import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { LabFlow } from './LabFlow'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { LabDemoProps } from './types'
import type { MultiAgentLocale } from '../../lib/locales'
import type { OntoNodeKind, OntoPathNode } from '../../types'

const ACCENT_VAR: Record<MultiAgentLocale['agents'][number]['accent'], string> = {
  amber: 'var(--color-orion-amber)',
  cyan: 'var(--color-orion-cyan)',
  violet: 'var(--color-orion-violet)',
  pass: 'var(--color-orion-pass)',
  info: 'var(--color-orion-info)',
}

const EVENT_INTERVAL_MS = 700

export function MultiAgentDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.multiAgent | undefined) ?? t.labDemos.multiAgent
  const [injected, setInjected] = useState(false)
  const [visibleCount, setVisibleCount] = useState(0)
  const timerRef = useRef<number | null>(null)

  const agentMap = useMemo(() => {
    const map = new Map<string, MultiAgentLocale['agents'][number]>()
    demo.agents.forEach((a) => map.set(a.id, a))
    return map
  }, [demo.agents])

  const path = useMemo<OntoPathNode[]>(() => {
    return demo.timeline.slice(0, visibleCount).map((e) => ({
      id: e.nodeId,
      kind: e.nodeKind,
      label: e.nodeLabel,
      detail: e.nodeDetail,
      parents: e.parents,
    }))
  }, [demo.timeline, visibleCount])

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
    }
  }, [])

  const inject = () => {
    setInjected(true)
    setVisibleCount(0)
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setVisibleCount((c) => {
        if (c >= demo.timeline.length) {
          if (timerRef.current) window.clearInterval(timerRef.current)
          return demo.timeline.length
        }
        return c + 1
      })
    }, EVENT_INTERVAL_MS)
  }

  const reset = () => {
    if (timerRef.current) window.clearInterval(timerRef.current)
    setInjected(false)
    setVisibleCount(0)
  }

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={injected ? demo.injectedLabel : demo.incidentLabel}
        >
          <IncidentCard demo={demo} />
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {!injected ? (
              <button
                type="button"
                onClick={inject}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-4 py-2 rounded-[var(--radius-sm)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium hover:bg-[color:var(--color-orion-amber-dim)] transition-colors"
              >
                {demo.injectButton}
              </button>
            ) : (
              <button
                type="button"
                onClick={reset}
                className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-2 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
              >
                {demo.resetButton}
              </button>
            )}
            <AgentLegend agents={demo.agents} />
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          {injected ? (
            <div className="grid lg:grid-cols-[360px_1fr] gap-4">
              <Timeline
                title={demo.timelineLabel}
                events={demo.timeline.slice(0, visibleCount)}
                agentMap={agentMap}
              />
              <Workspace
                title={demo.workspaceLabel}
                path={path}
                pathKey={`ma::${visibleCount}`}
                kindLabels={t.l3.kindLabels}
              />
            </div>
          ) : (
            <EmptyState title={demo.emptyStateTitle} body={demo.emptyStateBody} />
          )}
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

function IncidentCard({ demo }: { demo: MultiAgentLocale }) {
  return (
    <div
      className="rounded-[var(--radius-md)] p-3 border"
      style={{
        borderColor: 'color-mix(in oklch, var(--color-orion-block) 55%, transparent)',
        background: 'color-mix(in oklch, var(--color-orion-block) 8%, transparent)',
      }}
    >
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-block)] mb-1">
        {demo.incidentTitle}
      </div>
      <p
        className="text-[color:var(--color-orion-text)]"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.5 }}
      >
        {demo.incidentDetail}
      </p>
    </div>
  )
}

function AgentLegend({ agents }: { agents: MultiAgentLocale['agents'] }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {agents.map((a) => (
        <div key={a.id} className="flex items-center gap-1.5">
          <span
            className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[12px]"
            style={{
              background: `color-mix(in oklch, ${ACCENT_VAR[a.accent]} 22%, transparent)`,
              border: `1px solid ${ACCENT_VAR[a.accent]}`,
            }}
          >
            {a.icon}
          </span>
          <span
            className="mono text-[length:var(--text-micro)]"
            style={{ color: ACCENT_VAR[a.accent] }}
          >
            {a.name}
          </span>
        </div>
      ))}
    </div>
  )
}

function Timeline({
  title,
  events,
  agentMap,
}: {
  title: string
  events: MultiAgentLocale['timeline']
  agentMap: Map<string, MultiAgentLocale['agents'][number]>
}) {
  return (
    <div className="border-faint rounded-[var(--radius-md)] p-3 bg-[color:var(--color-orion-deep)]/30">
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
        {title}
      </div>
      <ol className="space-y-2.5">
        <AnimatePresence initial={false}>
          {events.map((e) => {
            const agent = agentMap.get(e.agentId)
            const accent = agent ? ACCENT_VAR[agent.accent] : 'var(--color-orion-text)'
            return (
              <motion.li
                key={e.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-start gap-3"
              >
                <span className="mono text-[10px] text-[color:var(--color-orion-haze)] pt-0.5 min-w-[60px]">
                  {e.timestamp}
                </span>
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[13px] shrink-0"
                  style={{
                    background: `color-mix(in oklch, ${accent} 22%, transparent)`,
                    border: `1px solid ${accent}`,
                  }}
                  title={agent?.name}
                >
                  {agent?.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className="mono text-[length:var(--text-micro)] uppercase tracking-widest"
                      style={{ color: accent }}
                    >
                      {e.verb}
                    </span>
                    <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)] break-words">
                      {e.nodeLabel}
                    </span>
                  </div>
                  <p
                    className="text-[color:var(--color-orion-haze)] leading-snug"
                    style={{ fontSize: 'var(--text-micro)' }}
                  >
                    {e.nodeDetail}
                  </p>
                </div>
              </motion.li>
            )
          })}
        </AnimatePresence>
      </ol>
    </div>
  )
}

function Workspace({
  title,
  path,
  pathKey,
  kindLabels,
}: {
  title: string
  path: OntoPathNode[]
  pathKey: string
  kindLabels: Record<OntoNodeKind, string>
}) {
  return (
    <div className="border-faint rounded-[var(--radius-md)] p-3 bg-[color:var(--color-orion-deep)]/30">
      <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-cyan)] mb-3">
        {title}
      </div>
      {path.length === 0 ? (
        <div className="min-h-[280px] flex items-center justify-center">
          <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-haze)]">
            …
          </span>
        </div>
      ) : (
        <LabFlow
          path={path}
          kindLabels={kindLabels}
          pathKey={pathKey}
          minHeight={320}
          maxHeight={440}
        />
      )}
    </div>
  )
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="min-h-[220px] flex flex-col items-center justify-center text-center px-6">
      <div className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2">
        {title}
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] max-w-md"
        style={{ fontSize: 'var(--text-small)', lineHeight: 1.6 }}
      >
        {body}
      </p>
    </div>
  )
}
